import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { ShieldCheck, ShieldAlert, CheckCircle2, XCircle, Loader2, Copy, Zap, Activity, Wallet } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

/**
 * Iter50 — Super-admin Tremendous Operations page.
 * - Readiness check (no secrets displayed, only booleans / IDs).
 * - Issue activation token per business with configurable caps.
 * - Kill switch toggle.
 * - Update caps on a business.
 * - Balance check + webhook health.
 * - Audit history.
 */
export default function SuperAdminTremendous() {
  const { user } = useAuth();
  const [readiness, setReadiness] = useState(null);
  const [readinessLoading, setReadinessLoading] = useState(false);
  const [webhookHealth, setWebhookHealth] = useState(null);
  const [businesses, setBusinesses] = useState([]);
  const [audit, setAudit] = useState([]);
  const [selectedBiz, setSelectedBiz] = useState("");
  const [issued, setIssued] = useState(null);
  const [caps, setCaps] = useState({ daily: 5000, monthly: 50000, low: 10000 });
  const [busy, setBusy] = useState(false);

  const loadReadiness = async () => {
    setReadinessLoading(true);
    try { const { data } = await api.get("/superadmin/tremendous/readiness"); setReadiness(data); }
    catch (e) { toast.error(e.response?.data?.detail || "Readiness check failed"); }
    finally { setReadinessLoading(false); }
  };
  const loadWebhookHealth = async () => {
    try { const { data } = await api.get("/superadmin/tremendous/webhook-health"); setWebhookHealth(data); } catch { setWebhookHealth(null); }
  };
  const loadBusinesses = async () => {
    try { const { data } = await api.get("/superadmin/businesses"); setBusinesses(data || []); if (!selectedBiz && data?.[0]) setSelectedBiz(data[0].id); } catch { setBusinesses([]); }
  };
  const loadAudit = async () => {
    try { const { data } = await api.get("/superadmin/tremendous/audit?limit=50"); setAudit(data.rows || []); } catch { setAudit([]); }
  };

  useEffect(() => { loadReadiness(); loadWebhookHealth(); loadBusinesses(); loadAudit(); }, []);

  if (!user?.is_super_admin) return <div className="p-8 text-zinc-500">Super-admin only.</div>;

  const issueToken = async () => {
    if (!selectedBiz) { toast.error("Select a business first"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/superadmin/tremendous/issue-activation-token", {
        business_id: selectedBiz,
        daily_cap_cents: parseInt(caps.daily, 10),
        monthly_cap_cents: parseInt(caps.monthly, 10),
        low_balance_threshold_cents: parseInt(caps.low, 10),
      });
      setIssued(data);
      toast.success("Activation token issued");
      loadAudit();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const runBalance = async () => {
    try { const { data } = await api.get("/superadmin/tremendous/balance-check"); toast.success(`Available: $${(data.available_cents / 100).toFixed(2)}`); } catch (e) { toast.error(e.response?.data?.detail || "Balance check failed"); }
  };

  const toggleKillSwitch = async (enabled) => {
    if (!selectedBiz) { toast.error("Select a business"); return; }
    if (enabled && !window.confirm("Enable kill switch for this business? Blocks ALL Tremendous fulfilment.")) return;
    try { await api.post("/superadmin/tremendous/kill-switch", { business_id: selectedBiz, enabled }); toast.success(`Kill switch ${enabled ? "on" : "off"}`); loadAudit(); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const updateCaps = async () => {
    if (!selectedBiz) return;
    try {
      await api.post("/superadmin/tremendous/update-caps", {
        business_id: selectedBiz,
        daily_cap_cents: parseInt(caps.daily, 10),
        monthly_cap_cents: parseInt(caps.monthly, 10),
        low_balance_threshold_cents: parseInt(caps.low, 10),
      });
      toast.success("Caps updated");
      loadAudit();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const runPilot = async () => {
    if (!selectedBiz) { toast.error("Select a business"); return; }
    const email = window.prompt("Pilot recipient email (super-admin controlled address):");
    if (!email) return;
    const name = window.prompt("Recipient display name:") || "Pilot Recipient";
    try { const { data } = await api.post("/superadmin/tremendous/pilot", { business_id: selectedBiz, recipient_email: email, recipient_name: name }); toast.success(`Pilot order ${data.order_id} @ $${data.denomination}`); loadAudit(); } catch (e) { toast.error(e.response?.data?.detail || "Pilot failed"); }
  };

  const markPilotComplete = async () => {
    if (!selectedBiz) return;
    if (!window.confirm("Mark pilot completed for this business? This unlocks normal production fulfilment.")) return;
    try { await api.post("/superadmin/tremendous/pilot/complete", { business_id: selectedBiz }); toast.success("Pilot marked complete"); loadAudit(); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const runSandboxPilot = async () => {
    if (!selectedBiz) { toast.error("Select a business"); return; }
    const email = window.prompt("Sandbox pilot recipient email (test address):");
    if (!email) return;
    const name = window.prompt("Recipient display name:") || "Sandbox Pilot";
    const rawCents = window.prompt("Sandbox amount in CENTS (must equal campaign minimum denomination):");
    const cents = parseInt(rawCents, 10);
    if (!cents || cents < 1) { toast.error("Invalid cents amount"); return; }
    if (!window.confirm(`Confirm sandbox pilot:\nrecipient=${email}\namount=${cents}¢ (must equal campaign minimum)\nProceed?`)) return;
    try {
      const { data } = await api.post("/superadmin/tremendous/run-sandbox-pilot", {
        business_id: selectedBiz, recipient_email: email, recipient_name: name,
        confirmed_amount_cents: cents,
      });
      toast.success(`Sandbox pilot ${data.pilot_id} — order ${data.order_id}`);
      const report = await api.get(`/superadmin/tremendous/sandbox-pilot/${data.pilot_id}/report`);
      const blob = new Blob([JSON.stringify(report.data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `tremendous-sandbox-pilot-${data.pilot_id}.json`;
      a.click(); URL.revokeObjectURL(url);
      loadAudit();
    } catch (e) { toast.error(e.response?.data?.detail || "Sandbox pilot failed"); }
  };

  const copy = (v) => { navigator.clipboard.writeText(v || ""); toast.success("Copied"); };

  const Pill = ({ ok, label }) => (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${ok ? "bg-emerald-100 text-emerald-700" : "bg-zinc-100 text-zinc-600"}`}>
      {ok ? <CheckCircle2 className="w-3 h-3"/> : <XCircle className="w-3 h-3"/>}
      {label}
    </span>
  );

  return (
    <div className="p-6 sm:p-8 space-y-6 max-w-6xl" data-testid="superadmin-tremendous-page">
      <div className="flex items-center gap-3">
        <ShieldCheck className="w-6 h-6 text-emerald-600"/>
        <div>
          <h1 className="font-heading text-2xl font-bold">Tremendous Operations</h1>
          <p className="text-sm text-zinc-500">Readiness gate, activation, kill switch, pilot, audit. Production keys are read-only via env vars — never displayed.</p>
        </div>
      </div>

      {/* Readiness */}
      <section className="bg-white border border-zinc-200 rounded-xl p-6 card-crisp" data-testid="readiness-section">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-heading text-lg font-bold flex items-center gap-2"><Activity className="w-5 h-5 text-emerald-700"/> Readiness</h2>
          <Button onClick={loadReadiness} disabled={readinessLoading} data-testid="run-readiness-btn">
            {readinessLoading && <Loader2 className="w-4 h-4 animate-spin mr-1"/>} Re-run readiness
          </Button>
        </div>
        {!readiness ? <Loader2 className="w-4 h-4 animate-spin"/> : (
          <div className="space-y-3">
            <div className="flex flex-wrap gap-2">
              <Pill ok={readiness.prod_enabled_env} label={readiness.prod_enabled_env ? "TREMENDOUS_PROD_ENABLED" : "Prod env off"} />
              <Pill ok={readiness.prod_configured} label={readiness.prod_configured ? "Env fully configured" : "Env incomplete"} />
              <Pill ok={readiness.organization_ok} label={readiness.organization_ok ? `Org: ${readiness.organization?.name || readiness.organization?.id}` : "Organization not reachable"} />
              <Pill ok={readiness.funding_source_ok} label={readiness.funding_source_ok ? `Funding OK (${readiness.funding_source?.available_cents ?? "?"}¢)` : "Funding source not ready"} />
              <Pill ok={readiness.campaign_ok} label={readiness.campaign_ok ? `Campaign: ${readiness.campaign?.name || readiness.campaign?.id}` : "Campaign not ready"} />
              <Pill ok={readiness.webhook_secret_set} label={readiness.webhook_secret_set ? "Webhook secret set" : "Webhook secret missing"} />
              <Pill ok={readiness.webhook_recently_active} label={readiness.webhook_recently_active ? "Recent webhook seen" : "No recent webhook"} />
              <Pill ok={readiness.ready} label={readiness.ready ? "READY FOR PRODUCTION" : "NOT READY"} />
            </div>
            {readiness.campaign_min_denomination_cents != null && (
              <div className="text-xs text-zinc-500">Campaign minimum denomination: ${(readiness.campaign_min_denomination_cents / 100).toFixed(2)}</div>
            )}
            {readiness.organization_error && <div className="text-xs text-red-700">Org error: {readiness.organization_error}</div>}
            {readiness.funding_source_error && <div className="text-xs text-red-700">Funding source error: {readiness.funding_source_error}</div>}
            {readiness.campaign_error && <div className="text-xs text-red-700">Campaign error: {readiness.campaign_error}</div>}
            <Button variant="outline" onClick={runBalance} data-testid="balance-check-btn"><Wallet className="w-4 h-4 mr-1"/> Fetch funding balance now</Button>
          </div>
        )}
      </section>

      {/* Webhook health */}
      <section className="bg-white border border-zinc-200 rounded-xl p-6 card-crisp" data-testid="webhook-section">
        <h2 className="font-heading text-lg font-bold mb-3 flex items-center gap-2"><Zap className="w-5 h-5 text-blue-600"/> Webhook health</h2>
        {!webhookHealth ? <Loader2 className="w-4 h-4 animate-spin"/> : (
          <div className="text-sm space-y-1">
            <div>Secret configured: <Pill ok={webhookHealth.secret_configured} label={webhookHealth.secret_configured ? "yes" : "no"} /></div>
            <div>Last event: {webhookHealth.last_event_at || "—"} ({webhookHealth.last_event_type || "—"})</div>
            <div>Last matched event: {webhookHealth.last_matched_event_at || "—"}</div>
            <div>Events last 24h / 30d: {webhookHealth.count_last_24h} / {webhookHealth.count_last_30d}</div>
            <div className="text-xs text-zinc-500 font-mono-accent">{webhookHealth.webhook_endpoint_hint}</div>
          </div>
        )}
      </section>

      {/* Business selection + activation */}
      <section className="bg-white border border-zinc-200 rounded-xl p-6 card-crisp space-y-3" data-testid="activation-section">
        <h2 className="font-heading text-lg font-bold flex items-center gap-2"><ShieldAlert className="w-5 h-5 text-amber-600"/> Business activation controls</h2>
        <label className="block text-sm">
          <div className="text-xs uppercase text-zinc-500 mb-1">Business</div>
          <select value={selectedBiz} onChange={e => setSelectedBiz(e.target.value)} className="w-full border rounded px-3 py-2" data-testid="business-select">
            <option value="">Select a business…</option>
            {businesses.map(b => <option key={b.id} value={b.id}>{b.business_name} ({b.id.slice(-6)})</option>)}
          </select>
        </label>
        <div className="grid grid-cols-3 gap-2">
          <label className="text-xs">Daily cap (¢)<input type="number" value={caps.daily} onChange={e => setCaps({...caps, daily: e.target.value})} className="w-full border rounded px-2 py-1 text-sm" data-testid="daily-cap-input"/></label>
          <label className="text-xs">Monthly cap (¢)<input type="number" value={caps.monthly} onChange={e => setCaps({...caps, monthly: e.target.value})} className="w-full border rounded px-2 py-1 text-sm" data-testid="monthly-cap-input"/></label>
          <label className="text-xs">Low balance (¢)<input type="number" value={caps.low} onChange={e => setCaps({...caps, low: e.target.value})} className="w-full border rounded px-2 py-1 text-sm" data-testid="low-balance-input"/></label>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button onClick={issueToken} disabled={busy || !readiness?.ready} data-testid="issue-token-btn">Issue activation token</Button>
          <Button variant="outline" onClick={updateCaps} data-testid="update-caps-btn">Update caps</Button>
          <Button variant="outline" onClick={() => toggleKillSwitch(true)} data-testid="kill-on-btn" className="text-red-700 border-red-300 hover:bg-red-50">Kill switch ON</Button>
          <Button variant="outline" onClick={() => toggleKillSwitch(false)} data-testid="kill-off-btn">Kill switch OFF</Button>
          <Button variant="outline" onClick={runPilot} disabled={!readiness?.ready} data-testid="run-pilot-btn">Run pilot order</Button>
          <Button variant="outline" onClick={runSandboxPilot} data-testid="run-sandbox-pilot-btn" className="text-emerald-700 border-emerald-300 hover:bg-emerald-50">Run Tremendous Sandbox Pilot</Button>
          <Button variant="outline" onClick={markPilotComplete} data-testid="mark-pilot-btn">Mark pilot complete</Button>
        </div>
        {issued && (
          <div className="border border-emerald-200 bg-emerald-50 rounded-md p-3 text-sm" data-testid="issued-token-block">
            <div>Token issued (expires {issued.expires_at}):</div>
            <div className="flex items-center gap-2 mt-1">
              <code className="bg-white px-2 py-1 rounded text-xs">{issued.token}</code>
              <Button variant="outline" size="sm" onClick={() => copy(issued.token)}><Copy className="w-3 h-3"/></Button>
            </div>
            <div className="text-xs text-emerald-800 mt-1">Confirmation phrase: <code>{issued.confirmation_phrase}</code></div>
          </div>
        )}
        {!readiness?.ready && <div className="text-xs text-amber-700">Readiness must pass before tokens can be issued or pilots run.</div>}
      </section>

      {/* Audit */}
      <section className="bg-white border border-zinc-200 rounded-xl p-6 card-crisp" data-testid="audit-section">
        <h2 className="font-heading text-lg font-bold mb-3">Audit history (last 50)</h2>
        {audit.length === 0 ? <div className="text-sm text-zinc-500">No audit rows yet.</div> : (
          <div className="text-xs font-mono-accent overflow-x-auto">
            <table className="w-full text-left">
              <thead className="border-b border-zinc-200"><tr>
                <th className="py-1 pr-3">At</th><th className="pr-3">Actor</th><th className="pr-3">Biz</th><th className="pr-3">From→To</th><th>Notes</th>
              </tr></thead>
              <tbody>
                {audit.map(a => (
                  <tr key={a.id} className="border-b border-zinc-100">
                    <td className="py-1 pr-3 whitespace-nowrap">{a.at}</td>
                    <td className="pr-3">{a.actor}</td>
                    <td className="pr-3 truncate max-w-[8ch]">{(a.business_id || "").slice(-6)}</td>
                    <td className="pr-3">{a.from_status || "·"} → {a.to_status || "·"}</td>
                    <td className="truncate max-w-[40ch]">{a.notes}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
